"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/providers/auth-provider";
import { UserAvatar } from "@/components/ui/user-avatar";
import { NotificationBell } from "@/components/notifications/notification-bell";
import { useCoinBalance } from "@/hooks/use-coin-balance";
import { useLogout } from "@/hooks/use-logout";
import { Coins, Settings, LogOut } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
interface NavbarAuthProps {
  avatarLink?: string;
  avatarSize?: number;
  avatarClassName?: string;
  coinsHref?: string;
  settingsHref?: string;
  /**
   * Hide the notification bell below md. Pass true ONLY on surfaces whose
   * mobile hamburger sheet carries its own Notifications entry (e.g. the
   * reader navbar) — surfaces without a hamburger (world header, checkout)
   * must keep the bell as their only mobile path to notifications.
   */
  hideBellOnMobile?: boolean;
}

/**
 * Auth button/status for navbars.
 * Uses the shared AuthProvider context — no independent Supabase calls.
 */
export function NavbarAuth(t0) {
  const $ = _c(26);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    avatarLink: t2,
    avatarSize: t3,
    avatarClassName: t4,
    coinsHref: t5,
    settingsHref: t6,
    hideBellOnMobile: t7
  } = t1;
  const avatarLink = t2 === undefined ? "/library" : t2;
  const avatarSize = t3 === undefined ? 32 : t3;
  const avatarClassName = t4 === undefined ? "navbar-auth__avatar" : t4;
  const coinsHref = t5 === undefined ? "/pricing" : t5;
  const settingsHref = t6 === undefined ? "/editor/settings" : t6;
  const hideBellOnMobile = t7 === undefined ? false : t7;
  const {
    user,
    profile,
    loading
  } = useAuth();
  const router = useRouter();
  const coins = useCoinBalance();
  const {
    isLoggingOut,
    startLogout
  } = useLogout();
  if (loading) {
    let t8;
    if ($[2] !== avatarSize) {
      t8 = <div className="rounded-full bg-muted animate-pulse" style={{
        width: avatarSize,
        height: avatarSize
      }} />;
      $[2] = avatarSize;
      $[3] = t8;
    } else {
      t8 = $[3];
    }
    return t8;
  }
  if (user) {
    const displayName = profile?.displayName ?? user.email ?? "";
    const t8 = profile?.avatarUrl;
    let t9;
    if ($[4] !== avatarClassName || $[5] !== avatarSize || $[6] !== displayName || $[7] !== t8) {
      t9 = <UserAvatar url={t8} name={displayName} size={avatarSize} className={avatarClassName} />;
      $[4] = avatarClassName;
      $[5] = avatarSize;
      $[6] = displayName;
      $[7] = t8;
      $[8] = t9;
    } else {
      t9 = $[8];
    }
    const avatarContent = t9;
    let t10;
    if ($[9] !== coins || $[10] !== coinsHref) {
      t10 = coins !== null && <Link href={coinsHref} className="flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1 text-sm font-bold text-brand transition-colors hover:bg-brand/20" title="Your Coin Balance"><Coins className="h-4 w-4" /><span>{coins.toLocaleString()}</span></Link>;
      $[9] = coins;
      $[10] = coinsHref;
      $[11] = t10;
    } else {
      t10 = $[11];
    }
    let t11;
    if ($[12] !== hideBellOnMobile) {
      t11 = hideBellOnMobile ? <span className="hidden md:block"><NotificationBell /></span> : <NotificationBell />;
      $[12] = hideBellOnMobile;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== avatarContent || $[15] !== avatarLink || $[16] !== isLoggingOut || $[17] !== router || $[18] !== settingsHref || $[19] !== startLogout) {
      t12 = avatarLink ? <Link href={avatarLink} aria-label="My Account">{avatarContent}</Link> : <DropdownMenu><DropdownMenuTrigger asChild={true}><button className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer">{avatarContent}</button></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-48"><DropdownMenuItem onSelect={() => router.push(settingsHref)} className="flex items-center gap-2 cursor-pointer"><Settings className="size-4" /><span>Settings</span></DropdownMenuItem><DropdownMenuSeparator /><DropdownMenuItem variant="destructive" className="cursor-pointer" disabled={isLoggingOut} onSelect={startLogout}><LogOut className="size-4" /><span>{isLoggingOut ? "Logging out..." : "Logout"}</span></DropdownMenuItem></DropdownMenuContent></DropdownMenu>;
      $[14] = avatarContent;
      $[15] = avatarLink;
      $[16] = isLoggingOut;
      $[17] = router;
      $[18] = settingsHref;
      $[19] = startLogout;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    let t13;
    if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12) {
      t13 = <div className="flex items-center gap-3">{t10}{t11}{t12}</div>;
      $[21] = t10;
      $[22] = t11;
      $[23] = t12;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    return t13;
  }
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Link href="/login" className="rounded-full bg-brand px-5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-hover">Sign In</Link>;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}